import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as ApproveIcon } from '@ubeya/shared-web/assets/confirmation.svg';
import { ReactComponent as RejectIcon } from '@ubeya/shared-web/assets/decline.svg';
import { ReactComponent as BaseApproveActiveIcon } from '@ubeya/shared-web/assets/confirmed.svg';
import { ReactComponent as BaseRejectActiveIcon } from '@ubeya/shared-web/assets/compliance-no.svg';
import { FlexMiddle, Tooltip } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import { isRequestEligibleForApproval } from 'shared/utils/requests';

const ButtonsContainer = styled(FlexMiddle)`
  gap: 6px;

  > svg {
    width: 25px;
    height: 25px;
  }
`;

const ApproveButton = styled(ApproveIcon)`
  cursor: pointer;

  path {
    fill: ${({ theme }) => theme.colors.grey4};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.greenPositive};
    path {
      fill: ${({ theme }) => theme.colors.greenPositive};
    }
  }
`;

const ApproveActiveIcon = styled(BaseApproveActiveIcon)`
  min-width: 20px;
`;

const RejectActiveIcon = styled(BaseRejectActiveIcon)`
  path:first-of-type {
    fill: ${({ theme }) => theme.colors.redNegative};
  }
`;

const RejectButton = styled(RejectIcon)`
  cursor: pointer;
  min-width: 20px;

  &:hover {
    color: ${({ theme }) => theme.colors.redNegative};
    path {
      fill: ${({ theme }) => theme.colors.redNegative};
    }
  }
`;

const CircleButton = ({ tooltip, onClick, children, className, disabled = false }) => (
  <Tooltip body={tooltip} disabled={!tooltip}>
    {React.cloneElement(children, {
      onClick: disabled ? undefined : onClick,
      className,
      style: disabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}
    })}
  </Tooltip>
);

const ActionRenderer = ({
  rowData: { id, status, managerReason: viewManagerReason, typeId },
  columnData: { approveRequest, setIsDeclineModalOpen }
}) => {
  const { t, enableContractFeature, enableContractLeaveManagementFeature, organizationId } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);

  const isDisableActions = useMemo(() => {
    const isEligible = isRequestEligibleForApproval({
      typeId,
      enableContractFeature,
      enableContractLeaveManagementFeature,
      myPrivileges,
      organizationId
    });
    return !isEligible;
  }, [enableContractFeature, enableContractLeaveManagementFeature, typeId, myPrivileges, organizationId]);

  const handleDecline = useCallback(() => setIsDeclineModalOpen({ requestIds: [id] }), [id, setIsDeclineModalOpen]);
  const handleApprove = useCallback(() => approveRequest(id), [id, approveRequest]);

  const approveTooltip = isDisableActions ? t('privilegeRequiredTooltip') : t('approve');
  const declineTooltip = isDisableActions ? t('privilegeRequiredTooltip') : t('decline');

  if (status === 0) {
    return (
      <ButtonsContainer>
        <CircleButton tooltip={approveTooltip} onClick={handleApprove} disabled={isDisableActions}>
          <ApproveButton />
        </CircleButton>

        <CircleButton tooltip={declineTooltip} onClick={handleDecline} disabled={isDisableActions}>
          <RejectButton />
        </CircleButton>
      </ButtonsContainer>
    );
  }

  if (status === 1) {
    return (
      <Tooltip body={t('approved')}>
        <ButtonsContainer>
          <CircleButton>
            <ApproveActiveIcon />
          </CircleButton>
        </ButtonsContainer>
      </Tooltip>
    );
  }

  return (
    <ButtonsContainer>
      <CircleButton tooltip={`${t('declineReason')}: ${viewManagerReason}`}>
        <RejectActiveIcon />
      </CircleButton>

      {/* {restoreButton} */}
    </ButtonsContainer>
  );
};

export default ActionRenderer;
